/**
 * Route-level guard that hides admin-only pages from non-admins.
 *
 * Wrap any ``<Route>`` element that should require admin and the gate
 * decides what to render based on ``/api/me``:
 *  - while loading, show a small placeholder so the route doesn't flash
 *    the page contents to a non-admin who's waiting on confirmation
 *  - non-admins are redirected to ``/search`` (the one open page) so
 *    a hand-typed URL doesn't 403-explode in the UI
 *  - admins fall through to the wrapped element
 *
 * The backend still enforces gating regardless of what the frontend does;
 * this is just for UX so users don't see broken pages.
 */
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { useMe } from "@/auth/useMe";

export function AdminGate({ children }: { children: ReactElement }) {
  const { data, isLoading } = useMe();

  if (isLoading) {
    return (
      <p className="font-sans text-sm text-bw-ink-muted">Checking permissions…</p>
    );
  }
  if (!data?.is_admin) {
    return <Navigate to="/search" replace />;
  }
  return children;
}
